import { assignOrgans } from "./assignment";
import { attachBlinkBorder } from "./blink-border";
import { type OnChange, type RenderedPanel, renderPanel } from "./controls";
import { HostGame, type HostPlayerInit } from "./game";
import type { NodeId } from "./graph";
import { NetHost, NetPeer } from "./net";
import { getOrgan } from "./organs";
import { renderStepper } from "./stepper";
import type { ControlValue, HostMsg, Instruction, PeerMsg } from "./types";

const GAME_TARGET = 14;
const GAME_DURATION_SEC = 180;
const BOLUS_OPTIONS = ["🥩 Steak", "🍞 Brot", "🍎 Apfel", "🍝 Pasta"];
const SELF_ID = "host";

const $ = <T extends HTMLElement>(id: string) => {
  const node = document.getElementById(id);
  if (!node) throw new Error(`Missing #${id}`);
  return node as T;
};

// Lobby elements
const lobbyView = $<HTMLElement>("lobby-view");
const gameView = $<HTMLElement>("game-view");
const btnHost = $<HTMLButtonElement>("btn-host");
const btnJoin = $<HTMLButtonElement>("btn-join");
const btnStart = $<HTMLButtonElement>("btn-start");
const codeInput = $<HTMLInputElement>("code");
const myCode = $<HTMLElement>("my-code");
const lobbyStatus = $<HTMLElement>("status");
const lobbyPeersEl = $<HTMLElement>("lobby-peers");

// Game elements
const blinkRoot = $<HTMLElement>("blink-border");
const stepperHost = $<HTMLElement>("stepper-host");
const instructionEl = $<HTMLElement>("instruction");
const panelsEl = $<HTMLElement>("panels");
const progressEl = $<HTMLElement>("progress");
const timerEl = $<HTMLElement>("timer");
const endOverlay = $<HTMLElement>("end-overlay");
const endMessage = $<HTMLElement>("end-message");
const btnPlayAgain = $<HTMLButtonElement>("btn-play-again");

// State
let netHost: NetHost | null = null;
let netPeer: NetPeer | null = null;
let isHost = false;
let myPeerId = "";
const connectedPeers = new Set<string>();
let hostGame: HostGame | null = null;
const panels = new Map<NodeId, RenderedPanel>();
let stepper: ReturnType<typeof renderStepper> | null = null;
let blink: ReturnType<typeof attachBlinkBorder> | null = null;
let lastBolusName = "🍞 Brot";
let currentStationLabel = "";

const STATION_LABELS: Record<NodeId, string> = {
  mund: "Mund",
  speiseroehre: "Speiseröhre",
  magen: "Magen",
  leber: "Leber",
  gallenblase: "Gallenblase",
  bauchspeicheldruese: "Bauchspeicheldrüse",
  zwoelffingerdarm: "Zwölffingerdarm",
  duenndarm: "Dünndarm",
  dickdarm: "Dickdarm",
  enddarm: "Enddarm",
};

function showLobby() {
  lobbyView.hidden = false;
  gameView.hidden = true;
}
function showGame() {
  lobbyView.hidden = true;
  gameView.hidden = false;
  endOverlay.hidden = true;
}

function setLobbyStatus(text: string, cls?: "ok" | "err") {
  lobbyStatus.textContent = text;
  lobbyStatus.className = cls ? `status ${cls}` : "status";
}

function renderLobbyPeers(peers: string[]) {
  lobbyPeersEl.textContent = "";
  for (const id of peers) {
    const li = document.createElement("li");
    li.textContent = id;
    lobbyPeersEl.appendChild(li);
  }
  btnStart.disabled = !isHost || peers.length < 1;
}

function randomCode(): string {
  return Math.random().toString(36).slice(2, 6).toUpperCase();
}

function randomBolus(): string {
  return BOLUS_OPTIONS[Math.floor(Math.random() * BOLUS_OPTIONS.length)];
}

// ---------- Host path ----------

btnHost.addEventListener("click", () => {
  const code = randomCode();
  isHost = true;
  myPeerId = SELF_ID;

  const h = new NetHost(code);
  netHost = h;
  setLobbyStatus(`Verbinde mit Server… (Code: ${code})`);

  h.on("open", () => {
    myCode.textContent = code;
    setLobbyStatus(`Host-Lobby offen. Code: ${code}`, "ok");
    btnStart.hidden = false;
    renderLobbyPeers([]);
  });
  h.on("peerJoin", (peerId) => {
    connectedPeers.add(peerId);
    renderLobbyPeers([...connectedPeers]);
    setLobbyStatus(`${connectedPeers.size} Mitspieler:in verbunden`, "ok");
  });
  h.on("peerMessage", (peerId, msg) => hostGame?.handlePeerMsg(peerId, msg));
  h.on("peerLeave", (peerId) => {
    connectedPeers.delete(peerId);
    hostGame?.removePlayer(peerId);
    renderLobbyPeers([...connectedPeers]);
    setLobbyStatus(`${connectedPeers.size} Mitspieler:in verbunden`);
  });
  h.on("error", (reason) => setLobbyStatus(`Fehler: ${reason}`, "err"));
  h.on("close", () => {
    if (netHost === h) {
      setLobbyStatus("Verbindung zum Server getrennt", "err");
      netHost = null;
    }
  });
});

btnStart.addEventListener("click", () => {
  if (!isHost || !netHost) return;
  startGameAsHost();
});

function startGameAsHost() {
  if (!netHost) return;
  const host = netHost;

  const bolusName = randomBolus();
  lastBolusName = bolusName;
  const peerIds = [myPeerId, ...connectedPeers];
  const assignment = assignOrgans(peerIds);

  hostGame = new HostGame({
    targetCompleted: GAME_TARGET,
    durationSec: GAME_DURATION_SEC,
    bolusName,
    onProgress: updateProgressUI,
    onEnd: showEnd,
  });

  const selfInit: HostPlayerInit = {
    peerId: myPeerId,
    organIds: assignment.get(myPeerId) ?? [],
    send: (msg) => receiveAsPlayer(msg),
  };
  hostGame.addPlayer(selfInit);

  for (const peerId of connectedPeers) {
    hostGame.addPlayer({
      peerId,
      organIds: assignment.get(peerId) ?? [],
      send: (msg) => host.sendTo(peerId, msg),
    });
  }

  showGame();
  setupGameUI();
  hostGame.start();
}

// ---------- Peer (joiner) path ----------

btnJoin.addEventListener("click", () => {
  const code = codeInput.value.trim().toUpperCase();
  if (!code) return;
  isHost = false;

  const p = new NetPeer(code);
  netPeer = p;
  setLobbyStatus(`Verbinde mit Server (Code ${code})…`);

  p.on("open", (peerId) => {
    myPeerId = peerId;
    setLobbyStatus("Verbunden. Warte auf Spielstart…", "ok");
    showGame();
    setupGameUI();
    instructionEl.textContent = "Warte auf Host…";
  });
  p.on("hostMessage", (msg) => handleHostMsg(msg));
  p.on("hostLeft", () => {
    setLobbyStatus("Host hat die Verbindung beendet.", "err");
    showLobby();
  });
  p.on("error", (reason) => setLobbyStatus(`Fehler: ${reason}`, "err"));
  p.on("close", () => {
    if (netPeer === p) {
      setLobbyStatus("Verbindung zum Server getrennt", "err");
      netPeer = null;
      showLobby();
    }
  });
});

// ---------- Game UI ----------

function setupGameUI() {
  if (!stepper) {
    stepper = renderStepper();
    stepperHost.textContent = "";
    stepperHost.appendChild(stepper.root);
  }
  if (!blink) blink = attachBlinkBorder(blinkRoot);
}

function receiveAsPlayer(msg: HostMsg) {
  handleHostMsg(msg);
}

function handleHostMsg(msg: HostMsg) {
  switch (msg.type) {
    case "assign":
      renderMyPanels(msg.peers.find((p) => p.peerId === myPeerId)?.organs ?? []);
      break;
    case "bolus":
      stepper?.update(msg.current, msg.active, msg.bolusName);
      lastBolusName = msg.bolusName;
      currentStationLabel = STATION_LABELS[msg.current];
      refreshFooter();
      break;
    case "instruction":
      if (msg.instruction) renderInstruction(msg.instruction);
      else clearInstruction();
      break;
    case "progress":
      updateProgressUI(msg.done, msg.target, msg.timeLeft);
      blink?.tick(msg.timeLeft);
      break;
    case "pulse": {
      if (msg.organId) panels.get(msg.organId)?.pulse(msg.ok);
      if (msg.organId) {
        const panel = panels.get(msg.organId);
        const root = panel?.root;
        if (root) {
          root.classList.remove("panel-pulse");
          void root.offsetWidth;
          root.classList.add("panel-pulse");
          setTimeout(() => root.classList.remove("panel-pulse"), 400);
        }
      }
      break;
    }
    case "end":
      showEnd(msg.done, msg.target, msg.bolusReachedStool);
      break;
  }
}

function renderMyPanels(organIds: NodeId[]) {
  panelsEl.textContent = "";
  panels.clear();
  panelsEl.style.setProperty("--panel-count", String(Math.max(1, organIds.length)));
  for (const id of organIds) {
    const organ = getOrgan(id);
    const onChange: OnChange = (controlId, value) => sendControl(id, controlId, value);
    const values: Record<string, ControlValue> = {};
    for (const c of organ.controls)
      values[c.id] = c.kind === "button" || c.kind === "lever" ? false : (c.min ?? 0);
    const panel = renderPanel(organ, values, onChange);
    panelsEl.appendChild(panel.root);
    panels.set(id, panel);
  }
}

function sendControl(organId: NodeId, controlId: string, value: ControlValue) {
  const msg: PeerMsg = { type: "control", organId, controlId, value };
  if (isHost) hostGame?.handlePeerMsg(myPeerId, msg);
  else netPeer?.send(msg);
}

function renderInstruction(ins: Instruction) {
  instructionEl.textContent = ins.text;
  instructionEl.classList.remove("flash");
  void instructionEl.offsetWidth;
  instructionEl.classList.add("flash");
}

function clearInstruction() {
  instructionEl.textContent = "— warte auf eine Anweisung von anderen —";
  instructionEl.classList.remove("flash");
}

let lastDone = 0;
let lastTimeLeft = 0;

function updateProgressUI(done: number, _target: number, timeLeft: number) {
  lastDone = done;
  lastTimeLeft = timeLeft;
  refreshFooter();
}

function refreshFooter() {
  const stationBit = currentStationLabel ? `Bolus bei ${currentStationLabel}` : "—";
  const organsBit = `Meine Organe: ${panels.size}`;
  progressEl.textContent = `${stationBit} · ${lastDone} Tasks · ${organsBit}`;
  const secs = Math.ceil(lastTimeLeft);
  const mm = String(Math.floor(secs / 60)).padStart(1, "0");
  const ss = String(secs % 60).padStart(2, "0");
  timerEl.textContent = `${mm}:${ss}`;
}

function showEnd(done: number, _target: number, bolusReachedStool?: boolean) {
  endOverlay.hidden = false;
  if (bolusReachedStool) {
    endMessage.textContent = `🎉 ${lastBolusName} verdaut! ${done} Tasks geschafft.`;
  } else {
    endMessage.textContent = `⏱ Zeit abgelaufen — ${done} Tasks geschafft. Chaos ist auch okay!`;
  }
  btnPlayAgain.hidden = !isHost;
}

btnPlayAgain.addEventListener("click", () => {
  if (isHost) startGameAsHost();
});

showLobby();
